import {Injectable} from '@angular/core';
import {GlobalService} from '../global/global.service';
import {Campus} from '../../model/Campus';
import {Helper} from '../../model/Helper';
import * as firebase from 'firebase';
import DocumentReference = firebase.firestore.DocumentReference;
import DocumentSnapshot = firebase.firestore.DocumentSnapshot;

@Injectable()
export class DataLoaderService {

  constructor(private globalService: GlobalService) {
  }

  public async FetchCampusList(): Promise<Campus[]> {
    const dataSnapshot = await this.globalService.GetCampusCollection().get();
    return Promise.all(dataSnapshot.docs
      .map(this.mapFirebaseDocToCampus.bind(this))
      .sort(this.sortCampusList.bind(this))
      .map<Promise<Campus>>(this.writeCoverAndAssetbundleURL.bind(this)));
  }

  public async FetchCampus(id: string): Promise<Campus> {
    const docSnapshot = await this.globalService.GetCampusCollection().doc(id).get();
    if (!docSnapshot.exists) {
      throw Error(`Campus with id ${id} not found.`);
    }
    const campus = this.mapFirebaseDocToCampus(docSnapshot);
    return this.writeCoverAndAssetbundleURL(campus);
  }

  public async AddNewCampus(id: string): Promise<string> {
    let doc: DocumentReference;

    if (id === '') {
      doc = this.globalService.GetCampusCollection().doc();
    } else {
      doc = this.globalService.GetCampusCollection().doc(id);
    }

    await doc.set({});
    return doc.id;
  }

  public RemoveCampus(id: string): Promise<void> {
    return this.globalService.GetCampusCollection().doc(id).delete();
  }

  public SaveCampus(id: string, campus: Campus): Promise<void> {
    return this.globalService.GetCampusCollection().doc(id).set(campus.AsData());
  }

  /*
    Cover
   */
  public async UploadCover(id: string, file: any): Promise<void> {
    await new Promise((resolve, reject) => {
      this.globalService
        .GetCoverBaseRef()
        .child(`${id}.png`)
        .put(file)
        .then(() => {
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async RemoveCover(id: string): Promise<void> {
    await this.globalService.GetCoverBaseRef().child(`${id}.png`).delete();
  }

  /*
    AssetBundles
   */
  public async UploadAssetBundle(id: string, platform: string, file: any): Promise<any> {
    await new Promise((resolve, reject) => {
      this.globalService
        .GetAssetBundleBaseRef()
        .child(platform)
        .child(`${id}.assetbundle`)
        .put(file)
        .then(() => {
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public async RemoveAssetBundle(id: string, platform: string): Promise<any> {
    await this.globalService.GetAssetBundleBaseRef().child(platform).child(`${id}.assetbundle`).delete();
  }

  public GetAssetbudleDownloadURL(id: string, platform: string): Promise<string> {
    return this.globalService.GetAssetBundleBaseRef().child(platform).child(id + '.assetbundle').getDownloadURL();
  }

  /*
    Private
   */
  private async writeCoverAndAssetbundleURL(campus: Campus): Promise<Campus> {
    try {
      campus.cover = await this.globalService.GetCoverBaseRef().child(campus.id + '.png').getDownloadURL();
    } catch (err) { /* ignore */
    }

    for (const platform of Helper.Platforms) {
      try {
        const ref = this.globalService.GetAssetBundleBaseRef().child(platform).child(`${campus.id}.assetbundle`);
        const url = await ref.getDownloadURL();
        campus.assetBundles[platform] = url;
      } catch (err) { /* ignore */
      }
    }

    return campus;
  }

  private mapFirebaseDocToCampus(doc: DocumentSnapshot): Campus {
    const campus = new Campus(doc.data());
    campus.id = doc.id;
    return campus;
  }

  private sortCampusList(a: Campus, b: Campus): number {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();

    if (a.released && !b.released) {
      return -1;
    } else if (!a.released && b.released) {
      return 1;
    } else {
      if (nameA < nameB) {
        return -1;
      } else if (nameA > nameB) {
        return 1;
      } else {
        return 0;
      }
    }
  }
}
